import { PLAN_LIMITS, type PlanName } from '@myog/shared';
import { Button } from '@myog/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { api, apiFailure } from '../lib/api-client.js';

/**
 * Page Billing.
 *
 * Tout ce qui touche à la carte, aux factures et à la résiliation est délégué
 * au portail Stripe : cette page ne fait que présenter l'état courant et
 * ouvrir les bonnes portes.
 *
 * Point important : au retour de Checkout, le plan n'est PAS encore à jour en
 * base — c'est le webhook qui le met à jour, et il peut arriver une seconde
 * après la redirection. On refetch donc activement pendant quelques secondes
 * plutôt que d'afficher un plan périmé.
 */

const PLAN_LABELS: Record<PlanName, string> = {
  free: 'Hobby',
  pro: 'Pro',
  agency: 'Agency',
};

const PLAN_PRICES: Record<PlanName, string> = {
  free: '0 €',
  pro: '9 € / mois',
  agency: '29 € / mois',
};

export function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const checkoutResult = searchParams.get('checkout');

  const billing = useQuery({
    queryKey: ['billing'],
    queryFn: async () => {
      const response = await api.v1.billing.$get();
      if (!response.ok) throw await apiFailure(response, 'Impossible de charger la facturation.');
      return response.json();
    },
  });

  // Le webhook Stripe peut arriver après la redirection : on interroge
  // brièvement jusqu'à voir le nouveau plan, plutôt que d'afficher un état
  // périmé qui donnerait l'impression que le paiement a échoué.
  useEffect(() => {
    if (checkoutResult !== 'success') return;

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      if (attempts >= 6) clearInterval(timer);
    }, 1500);

    return () => clearInterval(timer);
  }, [checkoutResult, queryClient]);

  const checkout = useMutation({
    mutationFn: async (plan: 'pro' | 'agency') => {
      const response = await api.v1.billing.checkout.$post({
        json: { plan, interval: 'month' },
      });
      if (!response.ok)
        throw await apiFailure(response, "La page de paiement n'a pas pu être ouverte.");
      return response.json();
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const portal = useMutation({
    mutationFn: async () => {
      const response = await api.v1.billing.portal.$post();
      if (!response.ok)
        throw await apiFailure(response, "Le portail de facturation n'a pas pu être ouvert.");
      return response.json();
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  if (billing.isPending) {
    return <div className="h-64 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />;
  }

  if (billing.isError) {
    return (
      <p className="rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
        {billing.error.message}
      </p>
    );
  }

  const current = billing.data.plan;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-text">Facturation</h1>
        <p className="mt-1 text-sm text-text-muted">
          Changement de formule, moyen de paiement et factures sont gérés par Stripe.
        </p>
      </header>

      {checkoutResult === 'success' ? (
        <p
          role="status"
          className="rounded-lg border border-success/30 bg-success/10 p-4 text-sm text-success"
        >
          Paiement confirmé. Votre nouvelle formule est active — si elle n'apparaît pas
          immédiatement ci-dessous, patientez quelques secondes.
        </p>
      ) : null}

      {checkoutResult === 'cancelled' ? (
        <p className="rounded-lg border border-border bg-surface p-4 text-sm text-text-muted">
          Paiement annulé. Aucun montant n'a été débité.
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="ml-2 text-accent hover:underline"
          >
            Fermer
          </button>
        </p>
      ) : null}

      <section
        aria-labelledby="plan-actuel"
        className="rounded-lg border border-border bg-surface p-6"
      >
        <h2 id="plan-actuel" className="text-sm font-medium text-text-muted">
          Formule actuelle
        </h2>
        <p className="mt-2 flex items-baseline gap-3">
          <span className="text-2xl font-semibold text-text">{PLAN_LABELS[current]}</span>
          <span className="text-sm text-text-muted">{PLAN_PRICES[current]}</span>
        </p>
        <p className="mt-2 text-sm text-text-muted">
          {PLAN_LIMITS[current].monthlyImages.toLocaleString('fr-FR')} images générées par mois ·{' '}
          {PLAN_LIMITS[current].requestsPerSecond} requêtes par seconde
        </p>
        <p className="mt-1 text-xs text-text-subtle">
          Les images déjà générées sont servies depuis le cache sans être décomptées.
        </p>

        {billing.data.renewsAt ? (
          <p className="mt-3 text-xs text-text-subtle">
            Prochain renouvellement le{' '}
            {new Date(billing.data.renewsAt * 1000).toLocaleDateString('fr-FR')}
          </p>
        ) : null}

        {billing.data.hasBillingAccount ? (
          <Button
            variant="secondary"
            className="mt-5"
            loading={portal.isPending}
            onClick={() => portal.mutate()}
          >
            Gérer mon abonnement
          </Button>
        ) : null}
      </section>

      {current === 'free' || current === 'pro' ? (
        <section aria-labelledby="upgrade">
          <h2 id="upgrade" className="mb-4 text-lg font-semibold text-text">
            Passer à une formule supérieure
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {(['pro', 'agency'] as const)
              .filter((plan) => plan !== current)
              .map((plan) => (
                <div key={plan} className="rounded-lg border border-border bg-surface p-5">
                  <h3 className="font-semibold text-text">{PLAN_LABELS[plan]}</h3>
                  <p className="mt-1 text-sm text-text-muted">{PLAN_PRICES[plan]}</p>
                  <p className="mt-3 text-sm text-text-muted">
                    {PLAN_LIMITS[plan].monthlyImages.toLocaleString('fr-FR')} images générées par
                    mois, sans watermark.
                  </p>
                  <Button
                    className="mt-5 w-full"
                    loading={checkout.isPending}
                    onClick={() => checkout.mutate(plan)}
                  >
                    Choisir {PLAN_LABELS[plan]}
                  </Button>
                </div>
              ))}
          </div>

          {checkout.isError ? (
            <p role="alert" className="mt-4 text-sm text-danger">
              {checkout.error.message}
            </p>
          ) : null}
        </section>
      ) : null}

      <p className="text-xs text-text-subtle">
        Le dépassement de quota n'interrompt jamais le service sur une formule payante : il est
        facturé à l'usage. Sur la formule gratuite, vos images passent sur un visuel de repli.
      </p>
    </div>
  );
}
